import { useStep } from '@/hooks/useStep';
import { useEffect, useMemo, useState } from 'react';
import {
	useNavigate,
	useOutletContext,
	useSearchParams,
} from 'react-router-dom';
import { decodeUrl } from '@/utils/encodeUrl';
import { StepSuccess, TimelineLayout, TimelineStepProps } from '@/components';
import { ProfessionalBookSessionStep } from './components';
import { professionalHasP2PPermission } from '@/utils/professionalPermissionTags';

type ProfessionalLayoutContext = {
	data: Professionals.Professional | undefined;
	isLoading: boolean;
};

interface StepConfig extends TimelineStepProps {
	visible?: boolean;
}

const CreateProfessionalSession = () => {
	const { currentStep, setTotalStep, setCurrentStep, previousStep } =
		useStep();
	const navigate = useNavigate();
	const { data: professional, isLoading: isProfessionalLoading } =
		useOutletContext<ProfessionalLayoutContext>();
	const [searchParams] = useSearchParams();
	const [isInitialized, setIsInitialized] = useState(false);
	const canSchedulePatientSessions =
		professionalHasP2PPermission(professional);

	const encodedParams = searchParams.get('d') || '';
	const decodedParams = encodedParams
		? decodeUrl<{ s?: string; session_id?: string }>(encodedParams)
		: null;
	const isEditMode = !!decodedParams?.session_id;

	const stepConfig: Array<StepConfig> = useMemo(
		() => [
			{
				title: isEditMode ? 'Editar consulta' : 'Agendar',
				step: 1,
				current: currentStep === 1,
				children: <ProfessionalBookSessionStep />,
				completed: currentStep > 1,
				visible: true,
			},
			{
				title: 'Sucesso',
				step: 2,
				current: currentStep === 2,
				children: <StepSuccess />,
				completed: currentStep === 2,
				isLast: true,
				visible: false,
			},
		],
		[currentStep, isEditMode],
	);

	const visualSteps = useMemo(() => {
		return stepConfig
			.filter((step) => step.visible)
			.map((step, index, arr) => ({
				...step,
				step: index + 1,
				isLast: index === arr.length - 1,
			}));
	}, [stepConfig]);

	const currentStepComponent = useMemo(() => {
		return stepConfig.find((step) => step.current)?.children;
	}, [stepConfig]);

	useEffect(() => {
		if (isProfessionalLoading) return;
		if (!canSchedulePatientSessions) {
			navigate('/profissional/home', { replace: true });
		}
	}, [isProfessionalLoading, canSchedulePatientSessions, navigate]);

	useEffect(() => {
		setTotalStep(stepConfig.length);
		const stepParam = searchParams.get('d');
		const decodedParams = stepParam
			? decodeUrl<{ s: string }>(stepParam)
			: { s: '1' };
		const parsedStep = Number.parseInt(decodedParams.s ?? '1', 10);
		const safeStep = Number.isNaN(parsedStep)
			? 1
			: Math.min(Math.max(parsedStep, 1), stepConfig.length);
		setCurrentStep(safeStep);
		setIsInitialized(true);
	}, []);

	if (
		!isInitialized ||
		isProfessionalLoading ||
		!canSchedulePatientSessions
	) {
		return null;
	}

	const handleBack = () => {
		if (currentStep === 1) {
			navigate('/profissional/home');
		} else {
			previousStep();
		}
	};

	return (
		<section className="w-full px-3 md:px-4">
			<TimelineLayout
				stepList={visualSteps}
				onBack={handleBack}
				compactSpacing
				hideStepProgress
			>
				{currentStepComponent}
			</TimelineLayout>
		</section>
	);
};

export { CreateProfessionalSession };
